import { HubConnection } from "@microsoft/signalr";
import { ElMessage, ElMessageBox } from "element-plus";
import { h, readonly, ref } from "vue";
import { authStore } from "~/stores/authStore";
import { hubManager } from "~/utils/hubManager";
import { SiteWideHubMessage } from "../api";
import { getMessageTime } from "../utils/miscUtils";

// Shared server offline state
const _isServerOffline = ref(false);
let siteWideHub: HubConnection | null;
const dynamicMessageText = ref("");

// Handler refs stored at module scope so unmountHandler can .off(event, handler)
// without using bare off (which would affect any other code listening on the shared "sitewide" hub).
let alertEveryoneHandler: ((message: SiteWideHubMessage) => void) | null = null;
let serverOfflineHandler: ((message: SiteWideHubMessage) => void) | null = null;
let serverOnlineHandler: ((message: SiteWideHubMessage) => void) | null = null;

export const useSiteWideHubManager = () => {
  const isServerOffline = readonly(_isServerOffline);

  // Function to update server offline status
  const setServerOffline = (offline: boolean) => {
    _isServerOffline.value = offline;
  };

  // Function to block requests when server is offline
  const blockRequestWhenOffline = (requestName?: string) => {
    if (_isServerOffline.value) {
      const message = requestName
        ? `Cannot perform "${requestName}" - server is offline for maintenance.`
        : "Server is offline for maintenance. Please try again when the server is back online.";

      ElMessage.warning({
        message,
        duration: getMessageTime(message),
        showClose: true,
      });

      // Return a rejected promise to prevent the request
      return Promise.reject(new Error("Server is offline"));
    }
    return null; // Allow request to proceed
  };

  // Setup hub connection and event handlers
  const setupHubConnection = async () => {
    if (!authStore.isAuthenticated) {
      return;
    }

    try {
      siteWideHub = await hubManager.getHub("sitewide", "/sitewide");

      if (!siteWideHub) {
        return;
      }

      alertEveryoneHandler = (message: SiteWideHubMessage) => {
        ElMessage.warning({
          message: message.text ?? "",
          grouping: true,
          showClose: true,
          duration: message.duration ?? getMessageTime(message.text ?? ""),
        });
      };
      serverOfflineHandler = (message: SiteWideHubMessage) => {
        setServerOffline(true);
        dynamicMessageText.value =
          message.text ?? "Server is offline for maintenance. Please wait";

        ElMessageBox({
          message: () => h("p", null, dynamicMessageText.value),
          type: "warning",
          showClose: false,
          showConfirmButton: false,
          closeOnClickModal: false,
          closeOnPressEscape: false,
          showCancelButton: false,
        });
      };
      serverOnlineHandler = (message: SiteWideHubMessage) => {
        setServerOffline(false);
        ElMessageBox.close();
        ElMessage.success({
          message: message.text ?? "",
          duration: message.duration ?? getMessageTime(message.text ?? ""),
          showClose: true,
        });
      };

      siteWideHub.on("AlertEveryone", alertEveryoneHandler);
      siteWideHub.on("ServerOffline", serverOfflineHandler);
      siteWideHub.on("ServerOnline", serverOnlineHandler);

      // Handle connection loss
      siteWideHub.onreconnecting((why?: Error) => {
        console.log(
          "Attempting to reconnect at",
          new Date().toLocaleTimeString(),
        );
        if (why) {
          console.warn("Reason:", why);
        }
        dynamicMessageText.value += ".";
      });

      // Handle automatic reconnection
      siteWideHub.onreconnected(() => {
        console.log(
          "Reconnected to server at",
          new Date().toLocaleTimeString(),
        );
        if (isServerOffline.value) {
          setServerOffline(false);
          ElMessageBox.close();
          ElMessage.success({
            message: "Server is back online!",
            duration: 5000,
            showClose: true,
          });
        }
      });
    } catch (error) {
      console.warn("Failed to setup hub connection:", error);
      // Don't throw - hub connection is not critical for app functionality
    }
  };

  const unmountHandler = () => {
    if (siteWideHub) {
      if (alertEveryoneHandler)
        siteWideHub.off("AlertEveryone", alertEveryoneHandler);
      if (serverOfflineHandler)
        siteWideHub.off("ServerOffline", serverOfflineHandler);
      if (serverOnlineHandler)
        siteWideHub.off("ServerOnline", serverOnlineHandler);
    }
    // Clear refs so a future setup can re-attach fresh (or same) handlers.
    alertEveryoneHandler = null;
    serverOfflineHandler = null;
    serverOnlineHandler = null;
  };
  return {
    isServerOffline,
    setServerOffline,
    setupHubConnection,
    blockRequestWhenOffline,
    siteWideHub,
    unmountHandler,
  };
};
